import { Box, Flex, Select, Text, useDisclosure } from "@chakra-ui/react";
import * as React from "react";

import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

// Custom components
import Card from "components/card/Card";
import ListTable, { LIST_MUTED_COLOR, ListHeader, ListRow } from "components/list/ListTable";
import { usePagination } from "components/list/TablePagination";
import { useForfaitUsage } from "hooks/useForfaitUsage";
import { useListState } from "hooks/useListState";
import { useSite } from "hooks/useSite";
import { nomsDesSites } from "utils";
import ModalForm from "components/modal/modalForm";
import Moment from "react-moment";
import FieldClient from "components/user/FieldClient";
import ForfaitRemaining, { LastIntervention } from "components/user/ForfaitRemaining";
import ListMenuForfait from "components/menu/ListMenuForfait";

type TableForfaitType = {
  id: string;
  idclient: string;
  date_debut: Date;
  client: string;
  prix: number;
  remarque_tech: string;
  statut: boolean;
  temps: number;
  type: number;
  sitesNoms?: string;
};

const columnHelper = createColumnHelper<TableForfaitType>();

// Ligne du tableau : le temps consommé n'est lu qu'une fois, pour les deux colonnes qui l'affichent
function ForfaitRow(props: { row: Row<TableForfaitType>; refresh: number }) {
  const { row, refresh } = props;
  const usage = useForfaitUsage(row.original.id, refresh);
  return <ListRow row={row} muted={!row.original.statut} cellContext={{ usage }} />;
}

// const columns = columnsDataCheck;
export default function ColumnTable(props: { tableData: any }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { tableData } = props;
  const [sorting, setSorting] = useListState<SortingState>("forfaits:tri", [
    { id: "date_debut", desc: true },
  ]);
  const [selectedForfait, setSelectedForfait] = React.useState<
    ForfaitType[] | null
  >(null);
  const [formModal, setFormModal] = React.useState("forfait");
  // Forfait qui vient de recevoir une intervention : seul son temps restant est recalculé
  const [refreshedForfait, setRefreshedForfait] = React.useState({ id: "", count: 0 });
  let defaultData = tableData;

  // Liste complète des forfaits ; le filtre de statut est appliqué à l'affichage
  const [allData, setAllData] = React.useState<any[]>(() => [...defaultData]);
  const updateData = React.useCallback((newData: any[]) => {
    setAllData(newData);
  }, []);
  // Colonnes créées une seule fois : de nouvelles fonctions `cell` à chaque rendu
  // feraient remonter toutes les cellules (et relancer leurs lectures Firestore)
  const columns = React.useMemo(
    () => [
      columnHelper.accessor("client", {
        id: "client",
        header: "Client",
        // Le nom est lu cellule par cellule : il n'y a pas de valeur à trier
        enableSorting: false,
        cell: (info) => (
          <>
            <Text fontWeight="700">
              <FieldClient
                id={info.row.original.idclient}
                field="raison_sociale"
                context="forfait"
              />
            </Text>
            {info.row.original.sitesNoms && (
              <Text color={LIST_MUTED_COLOR} fontSize="xs" lineHeight="16px">
                {info.row.original.sitesNoms}
              </Text>
            )}
            {info.row.original.remarque_tech && (
              <Text
                color={LIST_MUTED_COLOR}
                fontSize="xs"
                lineHeight="16px"
                noOfLines={2}
                title={info.row.original.remarque_tech}
              >
                {info.row.original.remarque_tech}
              </Text>
            )}
          </>
        ),
      }),
      columnHelper.accessor("statut", {
        id: "statut",
        header: "Statut",
        meta: { width: { base: "96px", "2xl": "11%" } },
        cell: (info) => (
          <Flex align="center" gap="6px">
            <Box
              w="8px"
              h="8px"
              borderRadius="50%"
              flexShrink={0}
              bg={info.getValue() ? "green.500" : "secondaryGray.500"}
            />
            {info.getValue() ? "Actif" : "Inactif"}
          </Flex>
        ),
      }),
      columnHelper.accessor("temps", {
        id: "temps",
        header: "Temps restant",
        meta: { width: { base: "220px", "2xl": "26%" } },
        // Le temps restant est lu ligne par ligne : il n'y a pas de valeur à trier
        enableSorting: false,
        cell: (info: any) => (
          <ForfaitRemaining forfait={info.row.original} usage={info.usage} />
        ),
      }),
      columnHelper.display({
        id: "derniere_intervention",
        header: "Dernière interv.",
        meta: { width: { base: "140px", "2xl": "15%" } },
        cell: (info: any) => <LastIntervention usage={info.usage} />,
      }),
      columnHelper.accessor("date_debut", {
        id: "date_debut",
        header: "Début",
        meta: { width: { base: "110px", "2xl": "13%" } },
        cell: (info: any) => (
          <Moment format="DD/MM/YYYY">{info.getValue().toDate()}</Moment>
        ),
      }),
      columnHelper.accessor("prix", {
        id: "prix",
        header: "Prix HT",
        meta: { width: { base: "90px", "2xl": "8%" }, align: "right" },
        cell: (info) => `${info.getValue()} €`,
      }),
      columnHelper.display({
        id: "actions",
        meta: { width: "67px", align: "right" },
        cell: (info) => (
          <ListMenuForfait
            forfaitrow={[info.row.original]}
            openmodal={onOpen}
            setselectedforfait={setSelectedForfait}
            setData={updateData}
            setFormModal={setFormModal}
          />
        ),
      }),
    ],
    [onOpen, updateData]
  );
  const [filterStatus, setFilterStatus] = useListState("forfaits:statut", "");
  // Les noms des sites sont lus une seule fois, pour toute la liste
  const [allSites, setAllSites] = React.useState<SiteType[]>([]);
  const { getSites } = useSite();
  React.useEffect(() => {
    let cancelled = false;
    getSites()
      .then((sites) => {
        if (!cancelled) {
          setAllSites(sites);
        }
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [getSites]);
  const data = React.useMemo(() => {
    const filtered = allData.filter(
      (forfait: { statut: boolean }) =>
        !filterStatus || forfait.statut === (filterStatus === "true")
    );
    return filtered.map((forfait: any) => ({
      ...forfait,
      sitesNoms: nomsDesSites(forfait.idsites, allSites) || forfait.site_internet,
    }));
  }, [allData, filterStatus, allSites]);


  const handleFormData = (newData: any[]) => {
    updateData(newData);
    // Un ajout de forfait revient à la première page, où le tri par défaut place la nouvelle ligne
    if (!selectedForfait) {
      table.setPageIndex(0);
    }
    if (formModal === "interventionForfait" && selectedForfait?.[0]) {
      const idForfait = selectedForfait[0].id;
      setRefreshedForfait((current) => ({ id: idForfait, count: current.count + 1 }));
    }
  };

  React.useEffect(() => {
    setAllData(tableData);
  }, [tableData]);

  const [pagination, setPagination] = usePagination(data.length);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting(updater);
      table.setPageIndex(0);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    // Garde la page après une modification ; le tri, les filtres et les ajouts reviennent à la première
    autoResetPageIndex: false,
    // Lignes identifiées par forfait : un tri ou un ajout ne remonte pas les cellules des autres lignes
    getRowId: (row) => row.id,
  });
  function handleAdd() {
    setSelectedForfait(null);
    setFormModal("forfait");
    onOpen();
  }
  function handleChangeStatut(event: React.ChangeEvent<HTMLSelectElement>) {
    setFilterStatus(event.target.value);
    table.setPageIndex(0);
  }

  return (
    <Card flexDirection="column" w="100%" px="0px" pt="16px" pb="8px">
      <ListHeader
        title={`Liste des Forfaits (${data.length})`}
        addLabel="Ajouter un Forfait"
        onAdd={handleAdd}
      >
        <Select
          aria-label="Filtrer par statut"
          size="sm"
          width="170px"
          borderRadius="10px"
          value={filterStatus}
          onChange={handleChangeStatut}
        >
          <option value={""}>Tous les statuts</option>
          <option value={"true"}>Actifs</option>
          <option value={"false"}>Inactifs</option>
        </Select>
      </ListHeader>
      <ListTable
        table={table}
        renderRow={(row) => (
          <ForfaitRow
            key={row.id}
            row={row}
            refresh={refreshedForfait.id === row.id ? refreshedForfait.count : 0}
          />
        )}
      />
      <ModalForm<ForfaitType>
        isOpen={isOpen}
        onClose={onClose}
        newData={handleFormData}
        updateData={selectedForfait}
        form={formModal}
      />
    </Card>
  );
}
